<template>
    <v-app id="app">
        <v-app-bar app color="primary" dark elevation="4" height="80">
            <v-toolbar-title class="d-flex align-center">
                <v-icon large class="mr-3">mdi-bike</v-icon>
                <div>
                    <div class="text-h5 font-weight-bold">MRT Kids</div>
                    <div class="text-caption">Control de Carreres</div>
                </div>
            </v-toolbar-title>

            <v-spacer></v-spacer>

            <v-chip
                v-if="sync.status === 'ready'"
                :color="sync.online ? 'success' : 'error'"
                class="mx-2"
                small
            >
                <v-icon small left>{{ sync.online ? 'mdi-cloud-check' : 'mdi-cloud-off-outline' }}</v-icon>
                {{ sync.online ? 'Connectat' : 'Sense connexió' }}
                <span v-if="sync.pending.length">&nbsp;· {{ sync.pending.length }} pendents de desar</span>
            </v-chip>

            <v-btn text to="/" large class="mx-2">
                <v-icon left>mdi-home</v-icon>
                Inici
            </v-btn>

            <v-btn 
                text 
                v-if="current_page_is_not_home" 
                @click="$router.go(-1)" 
                large 
                class="mx-2"
            >
                <v-icon left>mdi-arrow-left</v-icon>
                Enrere
            </v-btn>

            <v-btn text to="/categories" large class="mx-2">
                <v-icon left>mdi-format-list-bulleted</v-icon>
                Categories
            </v-btn>

            <v-btn text to="/lottery" large class="mx-2">
                <v-icon left>mdi-gift</v-icon>
                Sorteig
            </v-btn>

            <!-- <v-divider vertical class="mx-2"></v-divider>

            <v-btn text @click="importDialog = true" large class="mx-2">
                <v-icon left>mdi-upload</v-icon>
                Importar
            </v-btn> -->
        </v-app-bar>

        <!-- Diálogo de importación -->
        <v-dialog v-model="importDialog" max-width="600px">
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left dark>mdi-upload</v-icon>
                    Importar Corredors
                </v-card-title>
                
                <v-card-text class="pt-6">
                    <v-file-input
                        v-model="jsonFile"
                        accept=".json"
                        label="Selecciona el fitxer JSON"
                        prepend-icon="mdi-file-document"
                        outlined
                        dense
                        :disabled="importing"
                        @change="onFileSelected"
                        hide-details
                    ></v-file-input>
                    
                    <v-alert
                        v-if="importResult.show"
                        :type="importResult.type"
                        class="mt-4"
                        dismissible
                        @input="importResult.show = false"
                    >
                        {{ importResult.message }}
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        text
                        @click="closeImportDialog"
                        :disabled="importing"
                    >
                        Cancel·lar
                    </v-btn>
                    <v-btn
                        color="success"
                        text
                        :disabled="!jsonFile || importing"
                        :loading="importing"
                        @click="importRacers"
                    >
                        <v-icon left>mdi-import</v-icon>
                        Importar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-main>
            <v-container fluid class="pa-6">
                <transition v-if="sync.status === 'ready'" name="fade" mode="out-in">
                    <router-view />
                </transition>

                <div v-else-if="sync.status === 'loading'" class="text-center pa-12">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="mt-4">Carregant dades...</div>
                </div>

                <v-alert v-else-if="sync.status === 'config'" type="error" prominent>
                    Falta configurar Supabase: defineix <code>VUE_APP_SUPABASE_URL</code> i
                    <code>VUE_APP_SUPABASE_KEY</code> (mira <code>.env.example</code>) i torna a arrencar l'app.
                </v-alert>

                <v-alert v-else type="error" prominent>
                    No s'ha pogut connectar amb el servidor de dades i aquest navegador no en té cap còpia.
                    Comprova la connexió a Internet.
                    <template v-slot:append>
                        <v-btn color="white" outlined @click="$store.dispatch('init')">Reintentar</v-btn>
                    </template>
                </v-alert>
            </v-container>
        </v-main>

        <v-snackbar v-model="showNotice" color="error" :timeout="8000" top>
            {{ sync.notice }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showNotice = false">Tancar</v-btn>
            </template>
        </v-snackbar>

        <v-footer color="primary" dark padless>
            <v-row justify="center" no-gutters>
                <v-col class="text-center py-4" cols="12">
                    <div class="text-body-2">
                        Programat i dissenyat per 
                        <a href="mailto:lluismf88@gmail.com" class="white--text font-weight-bold">
                            Lluís Menéndez
                        </a>
                    </div>
                    <div class="text-caption mt-2">
                        © {{ new Date().getFullYear() }} - Sistema de Control de Carreres
                    </div>
                </v-col>
            </v-row>
        </v-footer>
    </v-app>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    data() {
        return {
            importDialog: false,
            jsonFile: null,
            importing: false,
            importResult: {
                show: false,
                type: 'success',
                message: ''
            },
            categoryMapping: {
                'MINI': 'c1',
                'PREB': 'c2',
                'BENJ': 'c3',
                'PRIN': 'c4',
                'ALEV': 'c5',
                'INF': 'c6'
            }
        };
    },
    computed: {
        current_page_is_not_home() {
            return this.$route.name != "home";
        },
        sync() {
            return this.$store.state.sync;
        },
        showNotice: {
            get() {
                return !!this.sync.notice;
            },
            set(value) {
                if (!value) this.$store.commit('sync/notify', null);
            },
        },
    },
    methods: {
        ...mapActions('racers', ['addRacer']),
        
        onFileSelected(file) {
            if (file) {
                console.log('Fitxer seleccionat:', file.name);
            }
        },

        closeImportDialog() {
            this.importDialog = false;
            this.jsonFile = null;
            this.importResult.show = false;
        },

        async importRacers() {
            if (!this.jsonFile) return;

            this.importing = true;
            this.importResult.show = false;

            try {
                const fileContent = await this.readFileAsText(this.jsonFile);
                const racers = JSON.parse(fileContent);

                if (!Array.isArray(racers)) {
                    throw new Error('El fitxer JSON no conté un array de corredors');
                }

                // Validar-ho tot abans d'esborrar res
                let valid = [];
                let errorCount = 0;
                for (const racer of racers) {
                    const categoryId = racer.categoria && this.categoryMapping[racer.categoria.nombre];
                    if (!categoryId) {
                        console.warn(`Corredor sense categoria vàlida:`, racer);
                        errorCount++;
                        continue;
                    }

                    valid.push({
                        name: racer.nombre,
                        surname: racer.apellidos,
                        number: racer.dorsal,
                        sex: racer.categoria.genero === 'Masculino' ? 'M' : 'F',
                        club: racer.club,
                        category: categoryId,
                    });
                }

                if (!valid.length) {
                    throw new Error('Cap corredor vàlid al fitxer');
                }

                await this.$store.dispatch('racers/clearRacers');
                for (const racer of valid) {
                    await this.addRacer(racer);
                }
                const importedCount = valid.length;

                this.importResult = {
                    show: true,
                    type: importedCount > 0 ? 'success' : 'error',
                    message: `S'han importat ${importedCount} corredors correctament.${errorCount > 0 ? ` Errors: ${errorCount}` : ''}`
                };

                // Limpiar el input después de importar con éxito
                if (importedCount > 0) {
                    setTimeout(() => {
                        this.closeImportDialog();
                    }, 2000);
                }

            } catch (error) {
                console.error('Error important JSON:', error);
                this.importResult = {
                    show: true,
                    type: 'error',
                    message: `Error important el fitxer: ${error.message}`
                };
            } finally {
                this.importing = false;
            }
        },

        readFileAsText(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsText(file);
            });
        }
    }
};
</script>

<style lang="scss">
#app {
    font-family: 'Roboto', 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #FEFEFE;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}

.v-application {
    background-color: #FEFEFE !important;
}
</style>
